import {
	ReactNode,
	SyntheticEvent,
	useCallback,
	useEffect,
	useState,useContext
} from "react";
import { useSearchParams } from "react-router-dom";

import { ShiftDashboard } from "./ShiftDashboard";
import ShiftHandover from "./ShiftHandover";
import ShiftLog from "./ShiftLog";
import ShiftOutOfService from "./ShiftOutOfService";
import ShiftSearch from "./ShiftSearch";
import { AdminProtected } from "@/app/application/AdminProtected";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import ShiftSettingsPage from "@/app/application/admin-menu/settings/ShiftSettingsPage";
import {
	ShiftPageContextProvider,
	useSharedSelectedGroup,
} from "@/features/shift/hooks";
import { ShiftTourSteps } from "@/features/shift/lib";
import {
	ShiftHeaderActionsContextProvider,
	ShiftPageHeader,
	ShiftRegisterDialog,
} from "@/features/shift/ui/components";
import { CustomTabPanel } from "@/shared/ui";
import { mobilePillTabsSx, sharedTabsSx } from "@/styles/sharedNavStyles";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import CloseIcon from "@mui/icons-material/Close";
import { useMediaQuery, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { useTour } from "@reactour/tour";
import dayjs from "dayjs";

import {
	useFetchShiftOptions,
	useFetchShiftSettings,
} from "@/service/ShiftHomeQueries";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


// The a11yProps function is a common naming convention for a helper function used to generate accessibility-related properties for components
function a11yProps(index: number) {
	return {
		id: `simple-tab-${index}`,
		"aria-controls": `simple-tabpanel-${index}`,
	};
}

const TAB_SLUGS: Record<number, string> = {
	1: "log",
	2: "handover",
	3: "outOfService",
	4: "search",
	7: "settings",
};
const TAB_FROM_SLUG: Record<string, number> = Object.fromEntries(
	Object.entries(TAB_SLUGS).map(([k, v]) => [v, Number(k)]),
);

type ShiftTab = {
	name: ReactNode;
	index: number;
	hasIcon: boolean;
	externalUrl?: string;
};

export const ShiftHome = () => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery("(max-width:767px)");
	const [searchParams, setSearchParams] = useSearchParams();
	const tabFromUrl = searchParams.get("tab");
	const valueFromUrl =
		tabFromUrl != null && TAB_FROM_SLUG[tabFromUrl] != null
			? TAB_FROM_SLUG[tabFromUrl]
			: 1;

	const [value, setValue] = useState(valueFromUrl);
	const [searchInput, setSearchInput] = useState("");
	const [openRegister, setOpenRegister] = useState(false);
	const [overviewOpen, setOverviewOpen] = useState(false);
	const [tabActions, setTabActions] = useState<ReactNode>(null);
	const [startFromUtc, setStartFromUtc] = useState("");
	const [endAtUtc, setEndAtUtc] = useState(() =>
		dayjs().format("YYYY-MM-DDTHH:mm"),
	);
	const [searchFilterOptions, setSearchFilterOptions] = useState({
		all: true,
		outOfService: false,
		handoverEvent: false,
	});
	const { setSteps, setCurrentStep } = useTour();

	useEffect(() => {
		setValue(valueFromUrl);
	}, [valueFromUrl]);

	const { selectedGroup, setSelectedGroup, groupIdOrUndefined } =
		useSharedSelectedGroup();
	const { data: shiftOptions, isLoading: optionsLoading } =
		useFetchShiftOptions();
	const { data: ShiftSetting } = useFetchShiftSettings();

	useEffect(() => {
		setCurrentStep(0);
		if (setSteps) setSteps(ShiftTourSteps);
	}, [setSteps, setCurrentStep]);

	const handleGroupChange = useCallback(
		(v: string) => setSelectedGroup(v),
		[setSelectedGroup],
	);
	const handleCloseOverview = useCallback(() => {
		setOverviewOpen(false);
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.delete("period");
				return next;
			},
			{ replace: true },
		);
	}, [setSearchParams]);
	const closeOverviewOnly = useCallback(() => {
		setOverviewOpen(false);
	}, []);

	const externalLinks: Record<
		"ExternalLinkOne" | "ExternalLinkTwo" | "ExternalLinkThree",
		| (typeof ShiftSetting extends null | undefined
				? never
				: typeof ShiftSetting extends infer T
					? T extends {
							ExternalLinkOne?: infer A;
							ExternalLinkTwo?: infer B;
							ExternalLinkThree?: infer C;
						}
						? A | B | C
						: never
					: never)
		| undefined
	> = {
		ExternalLinkOne: ShiftSetting?.ExternalLinkOne,
		ExternalLinkTwo: ShiftSetting?.ExternalLinkTwo,
		ExternalLinkThree: ShiftSetting?.ExternalLinkThree,
	};
	const tabs: ShiftTab[] = [
		{
			name: <Translate String="Shift.Event.ShiftLog">Shift Log</Translate>,
			index: 1,
			hasIcon: false,
		},
		{
			name: <Translate String="Shift.Event.Handover">Handover</Translate>,
			index: 2,
			hasIcon: false,
		},
		{
			name: (
				<Translate String="Shift.Event.OutOfService">Out of Service</Translate>
			),
			index: 3,
			hasIcon: false,
		},
		{
			name: <Translate String="Shift.Event.Search">Search</Translate>,
			index: 4,
			hasIcon: false,
		},
		...(ShiftSetting
			? (Object.keys(externalLinks) as Array<keyof typeof externalLinks>)
					.filter((key) => externalLinks[key]?.Name && externalLinks[key]?.Url)
					.map((key, idx) => ({
						name: (
							<Translate String={`Shift.Event.${key}`}>
								{externalLinks[key]?.Name}
							</Translate>
						),
						index: 5 + idx,
						hasIcon: true,
						externalUrl: externalLinks[key]?.Url, // Adding a URL to handle redirection
					}))
			: []),
	];

	const handleChange = (_event: SyntheticEvent, newValue: number) => {
		const externalTab = tabs.find((tab) => tab.index === newValue)?.externalUrl;
		if (externalTab) {
			window.open(externalTab, "_blank");
		} else {
			setValue(newValue);
			const slug = TAB_SLUGS[newValue];
			if (slug) {
				setSearchParams({ tab: slug }, { replace: true });
			}
		}
	};

	return (
		<ShiftPageContextProvider
			value={{
				searchInput,
				setSearchInput,
				openRegister,
				setOpenRegister,
				selectedGroup,
				setSelectedGroup,
				groupIdOrUndefined,
				startFromUtc,
				setStartFromUtc,
				endAtUtc,
				setEndAtUtc,
				searchFilterOptions,
				setSearchFilterOptions,
			}}
		>
			<ShiftHeaderActionsContextProvider value={setTabActions}>
				<Box
					data-tour="shift-page"
					sx={{
						width: "100%",
						flex: 1,
						minHeight: 0,
						display: "flex",
						flexDirection: "column",
						bgcolor: "background.default",
					}}
				>
					{/* Header + Tabs — unified block like Procedures */}
					<ShiftPageHeader
						onRegisterClick={() => setOpenRegister(true)}
						onOverviewClick={() => setOverviewOpen(true)}
						selectedGroup={selectedGroup}
						onGroupChange={handleGroupChange}
						searchInput={searchInput}
						onSearchChange={setSearchInput}
						extraActions={tabActions}
						shiftOptions={shiftOptions ?? undefined}
						loading={optionsLoading}
						activeTabIndex={value}
						startFromUtc={startFromUtc}
						setStartFromUtc={setStartFromUtc}
						endAtUtc={endAtUtc}
						setEndAtUtc={setEndAtUtc}
						searchFilterOptions={searchFilterOptions}
						setSearchFilterOptions={setSearchFilterOptions}
						tabs={
							<Tabs
								data-tour="shift-tabs"
								variant="scrollable"
								scrollButtons="auto"
								allowScrollButtonsMobile
								value={value}
								onChange={handleChange}
								aria-label={GetLanguageValueAsString(translation, "Auto.Features.Shift.Pages.ShiftHome.L265C20.Shift", "Shift")}
								textColor="inherit"
								indicatorColor="primary"
								sx={isMobile ? mobilePillTabsSx(theme) : sharedTabsSx}
							>
								{tabs.map((el, index) => (
									<Tab
										key={index}
										value={el.index}
										label={el.name}
										{...a11yProps(el.index)}
										icon={
											el.hasIcon ? <ArrowOutwardIcon fontSize="small" /> : ""
										}
										iconPosition="end"
									/>
								))}
							</Tabs>
						}
					/>
					<Box
						data-tour="shift-content"
						sx={{
							flex: 1,
							minHeight: 0,
							overflow: "hidden",
							display: "flex",
							flexDirection: "column",
						}}
					>
						{tabs.map(
							(tab, index) =>
								!tab.externalUrl && (
									<CustomTabPanel
										key={index}
										value={value}
										index={tab.index}
										fill
									>
										{tab.index === 1 && <ShiftLog />}
										{tab.index === 2 && <ShiftHandover />}
										{tab.index === 3 && <ShiftOutOfService />}
										{tab.index === 4 && <ShiftSearch />}
									</CustomTabPanel>
								),
						)}
						<AdminProtected>
							<CustomTabPanel value={value} index={7} fill>
								<ShiftSettingsPage />
							</CustomTabPanel>
						</AdminProtected>
					</Box>
				</Box>
				<ShiftRegisterDialog
					page="ShiftLog"
					open={openRegister}
					onClose={() => setOpenRegister(false)}
					selectedGroup={selectedGroup}
				/>
				<Dialog
					open={overviewOpen}
					onClose={handleCloseOverview}
					maxWidth="xl"
					fullWidth
				>
					<DialogTitle
						sx={{
							position: "relative",
							pr: 6,
							minWidth: 0,
						}}
					>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								gap: 1,
								minWidth: 0,
							}}
						>
							<Typography
								variant="h6"
								fontWeight={800}
								sx={{ minWidth: 0, flex: 1 }}
								noWrap
							>
								<Translate String="Shift.Dashboard.Tab">
									Shift dashboard
								</Translate>
							</Typography>
							<FormControl
								size="small"
								sx={{ width: { xs: 140, sm: 200, md: 240 }, flexShrink: 0 }}
							>
								<Select
									value={selectedGroup}
									onChange={(e) => handleGroupChange(e.target.value)}
									sx={{
										"& .MuiOutlinedInput-notchedOutline": { borderRadius: 2 },
									}}
								>
									<MenuItem value="all">
										<Translate String="Shift.Event.AllGroups">
											All Groups
										</Translate>
									</MenuItem>
									{shiftOptions?.Groups?.map((item) => (
										<MenuItem key={item.Id} value={String(item.Id)}>
											{item.Name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Box>
						<IconButton
							onClick={handleCloseOverview}
							aria-label={GetLanguageValueAsString(translation, "Auto.Features.Shift.Pages.ShiftHome.L382C19.Close", "Close")}
							size="small"
							sx={{ position: "absolute", top: 12, right: 8 }}
						>
							<CloseIcon fontSize="small" />
						</IconButton>
					</DialogTitle>
					<DialogContent
						dividers
						sx={{
							p: 1,
							height: "80vh",
							display: "flex",
							flexDirection: "column",
							minHeight: 0,
						}}
					>
						<Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
							<ShiftDashboard onNavigateFromOverview={closeOverviewOnly} />
						</Box>
					</DialogContent>
				</Dialog>
			</ShiftHeaderActionsContextProvider>
		</ShiftPageContextProvider>
	);
};
